import type {
  UppFloorPlanElementClickPayload,
  UppFloorPlanElementGeometryPayload,
  UppFloorPlanStageComponent,
} from '../canvas/upp-floor-plan-stage.component';
import type { UppKonvaFloorPlanModel } from './upp-konva-floor-plan-model';
import type { UppKonvaSceneElementRef } from './upp-konva-scene-ref';
/**
 * English comment normalized.
 * Used by **`upp-konva`**, the lab demo and package specs.
 */
const _floorByModel = new WeakMap<UppKonvaFloorPlanModel, UppFloorPlanStageComponent>();

export type UppKonvaStageHostHooks = {
  onSelect?: (elementUuids: readonly string[]) => void;
  onElementActivate?: (payload: UppFloorPlanElementClickPayload) => void;
  onGeometryCommit?: (payload: UppFloorPlanElementGeometryPayload) => void;
};

const _hooksByModel = new WeakMap<UppKonvaFloorPlanModel, UppKonvaStageHostHooks>();

export function bindUppKonvaStageFloor(model: UppKonvaFloorPlanModel, floor: UppFloorPlanStageComponent | null): void {
  if (floor === null) {
    _floorByModel.delete(model);
  } else {
    _floorByModel.set(model, floor);
  }
}

/** Lab / tests: callbacks that the host invokes (not product API). */
export function setUppKonvaStageHooks(model: UppKonvaFloorPlanModel, hooks: UppKonvaStageHostHooks | null): void {
  if (hooks === null) {
    _hooksByModel.delete(model);
  } else {
    _hooksByModel.set(model, hooks);
  }
}

export function stageInvokeSelection(model: UppKonvaFloorPlanModel, elementUuids: readonly string[]): void {
  _hooksByModel.get(model)?.onSelect?.(elementUuids);
}

export function stageInvokeActivate(model: UppKonvaFloorPlanModel, payload: UppFloorPlanElementClickPayload): void {
  _hooksByModel.get(model)?.onElementActivate?.(payload);
}

export function stageInvokeGeometry(model: UppKonvaFloorPlanModel, payload: UppFloorPlanElementGeometryPayload): void {
  _hooksByModel.get(model)?.onGeometryCommit?.(payload);
}

export function stageSelectOnly(model: UppKonvaFloorPlanModel, uuids: readonly string[]): void {
  _floorByModel.get(model)?.selectOnly([...uuids]);
}

export function stageClearSelection(model: UppKonvaFloorPlanModel): void {
  _floorByModel.get(model)?.clearSelection();
}

export function stageGetSelection(model: UppKonvaFloorPlanModel): readonly UppKonvaSceneElementRef[] {
  return _floorByModel.get(model)?.getSelection() ?? [];
}
